/**
 * Sorting and filtering for the all-materials table (Phase 16).
 *
 * Extracted rather than inlined because every column sorts differently: two of
 * them sort by a name resolved from another table, cost is a computed extended
 * total rather than a stored column, and status has a meaningful workflow order
 * that is not alphabetical. That's a pile of small decisions worth pinning.
 */

import type { SpecRow } from '@/types/database';
import { parseDateOnly } from '@/lib/dates';
import { scaleDecimal } from '@/lib/money';

export type SpecSortKey =
  | 'item' | 'room' | 'vendor' | 'category' | 'cost' | 'status' | 'delivery';
export type SortDir = 'asc' | 'desc';

/** Name resolution the sorter can't do on its own. */
export interface SpecTableContext {
  roomName(roomId: string): string;
  categoryName(categoryId: string | null | undefined): string;
}

export interface SpecFilters {
  /** A material_categories.id, or 'all'. */
  categoryId?: string;
  /** A rooms.id, or 'all'. */
  roomId?: string;
}

/**
 * Status sorts by workflow position, not alphabetically.
 *
 * Alphabetical would put Approved before Proposed and Backordered first, which
 * tells a designer nothing. This order is how a material actually progresses,
 * with the two dead ends last.
 */
const STATUS_ORDER: Record<SpecRow['status'], number> = {
  proposed: 0, approved: 1, ordered: 2, shipped: 3,
  delivered: 4, installed: 5, backordered: 6, rejected: 7,
};

/** Extended cost — what the line actually costs, not the unit price. */
export function extendedCostCents(s: Pick<SpecRow, 'unit_cost_cents' | 'quantity'>): number | null {
  if (s.unit_cost_cents == null) return null;
  return Math.round(s.unit_cost_cents * Number(s.quantity));
}

/** Largest value `quantity numeric(10,2)` can hold. */
const MAX_QUANTITY = 99_999_999.99;

/**
 * Quantity as typed by a human → a number the database will accept, or null.
 *
 * Null means "don't write this" rather than "set it to zero": `specs.quantity`
 * is NOT NULL with a default of 1, so a blank or garbled field must leave the
 * existing value alone. Silently resetting a quantity to 1 would change what a
 * line costs without the designer touching the price.
 *
 * Zero IS accepted — "none of this after all" is a real thing to record, and
 * refusing it would be paternalistic.
 */
export function parseQuantity(raw: string): number | null {
  // Same exact-decimal path as money, for the same reason: Math.round on a
  // float product rounds half-values inconsistently.
  const scaled = scaleDecimal(raw, 2);
  if (scaled === null) return null;
  const n = scaled / 100;
  return n > MAX_QUANTITY ? null : n;
}

export function filterSpecs(specs: SpecRow[], f: SpecFilters = {}): SpecRow[] {
  return specs.filter((s) => {
    if (f.roomId && f.roomId !== 'all' && s.room_id !== f.roomId) return false;
    if (f.categoryId && f.categoryId !== 'all') {
      // 'none' is a real choice: "which materials have I not categorized yet".
      if (f.categoryId === 'none') return s.material_category_id == null;
      if (s.material_category_id !== f.categoryId) return false;
    }
    return true;
  });
}

/**
 * Sort a copy of `specs`. Never mutates the input.
 *
 * Missing values always sort last, in BOTH directions. Flipping to descending
 * to see the most expensive items should not bury them under a wall of blanks.
 */
export function sortSpecs(
  specs: SpecRow[],
  key: SpecSortKey,
  dir: SortDir,
  ctx: SpecTableContext,
): SpecRow[] {
  const sign = dir === 'asc' ? 1 : -1;

  const cmp = (a: SpecRow, b: SpecRow): number => {
    switch (key) {
      case 'cost': {
        const av = extendedCostCents(a);
        const bv = extendedCostCents(b);
        const n = nullsLast(av, bv);
        return n ?? sign * ((av as number) - (bv as number));
      }
      case 'delivery': {
        const av = dateValue(a.estimated_delivery);
        const bv = dateValue(b.estimated_delivery);
        const n = nullsLast(av, bv);
        return n ?? sign * ((av as number) - (bv as number));
      }
      case 'status':
        return sign * (STATUS_ORDER[a.status] - STATUS_ORDER[b.status]);
      case 'room':
        return sign * text(ctx.roomName(a.room_id)).localeCompare(text(ctx.roomName(b.room_id)));
      case 'category': {
        // Uncategorized sorts last rather than first, so the table opens on
        // real categories instead of a block of blanks.
        const av = ctx.categoryName(a.material_category_id);
        const bv = ctx.categoryName(b.material_category_id);
        const n = nullsLast(av || null, bv || null);
        return n ?? sign * av.localeCompare(bv);
      }
      case 'vendor': {
        const n = nullsLast(a.vendor || null, b.vendor || null);
        return n ?? sign * text(a.vendor).localeCompare(text(b.vendor));
      }
      case 'item':
      default:
        return sign * a.item.localeCompare(b.item);
    }
  };

  // Stable tiebreak on item, so equal keys don't reshuffle between renders.
  return [...specs].sort((a, b) => cmp(a, b) || a.item.localeCompare(b.item));
}

/** Returns a comparison when either side is missing, else null. */
function nullsLast(a: unknown, b: unknown): number | null {
  const an = a == null;
  const bn = b == null;
  if (an && bn) return 0;
  if (an) return 1;
  if (bn) return -1;
  return null;
}

function dateValue(iso: string | null): number | null {
  if (!iso) return null;
  const d = parseDateOnly(iso);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
}

function text(v: string | null | undefined): string {
  return v ?? '';
}

/** Next sort state when a header is clicked: same column flips, new column starts ascending. */
export function nextSort(
  current: { key: SpecSortKey; dir: SortDir },
  clicked: SpecSortKey,
): { key: SpecSortKey; dir: SortDir } {
  if (current.key !== clicked) return { key: clicked, dir: 'asc' };
  return { key: clicked, dir: current.dir === 'asc' ? 'desc' : 'asc' };
}
